import { call, put } from 'redux-saga/effects';
import { getAxios, getAxiosWithoutLoading } from './axios';
import { Endpoints, Messages } from '../config';
import {
  getDefaultHeaders,
  getNotificationId,
  getTokenHeaders,
} from './helper';
import { Client } from '@passwordlessdev/passwordless-client';
import * as Types from '../store/actions/types';
import { errorCatch } from '../store/sagas/helper.saga';
import * as Notifications from '../config/notifications';
import { displayNotification } from '../store/actions/notification';
import { getEnvVar } from '../helpers/getEnvVar';
import { createIntl, createIntlCache } from 'react-intl'
import { store } from '../store/store';
import { BITWARDEN_ERROR_TITLES_MESSAGES } from '../config/constants';

export function* login(payload) {
  return yield call(
    getAxios().post,
    Endpoints.ENDPOINT_LOGIN,
    payload,
    getDefaultHeaders()
  );
}

export function* getUserDetails() {
  return yield call(
    getAxios().get,
    Endpoints.ENDPOINT_USER_PROFILE,
    getTokenHeaders()
  );
}

export function* logoutUser(payload) {
  return yield call(
    getAxios().post,
    Endpoints.ENDPOINT_LOGOUT,
    payload,
    getTokenHeaders()
  );
}

export function* forgot(payload) {
  return yield call(
    getAxios().post,
    Endpoints.ENDPOINT_FORGOT,
    payload,
    getDefaultHeaders()
  );
}

export function* reset(payload) {
  return yield call(
    getAxios().put,
    (Endpoints.ENDPOINT_FORGOT + '/' + payload.token),
    payload,
    getDefaultHeaders()
  );
}

export function* accountValidation(payload) {
  const { userId, verificationHash, signature, expires } = payload;

  return yield call(
    getAxios().put,
    (Endpoints.ENDPOINT_ACCOUNT_VERIFY + userId + '/' + verificationHash + '?signature=' + signature + '&expires=' + expires),
    null,
    getDefaultHeaders()
  );
}

export function* resendEmail(payload) {
  return yield call(
    getAxios().post,
    Endpoints.ENDPOINT_RESEND_VERIFY,
    payload,
    getDefaultHeaders()
  );
}

export function* updateUserProfile(payload) {
  return yield call(
    getAxios().put,
    Endpoints.ENDPOINT_USER_PROFILE,
    payload,
    getTokenHeaders()
  );
}

export function* registerPasskey(payload)  {
  const notificationId = Notifications.NOTIFICATIONS_CHANGE_ACCOUNT_SECURITY;
  const cache = createIntlCache()

  const intl = createIntl({
    locale: store.getState().locale.lang,
    messages: store.getState().locale.messages,
  }, cache);
  
  try {
    const response = yield call(registerPasskeyApi, payload.passkey_token);
    
    if (response) { 
      return response;
    }
  } catch (e) {
    yield errorCatch(e, {
      400: function* () {
        let message = BITWARDEN_ERROR_TITLES_MESSAGES[e.response.data.message.passkey_auth[0]] ? 
          { passkey_auth: [intl.formatMessage({ id: BITWARDEN_ERROR_TITLES_MESSAGES[e.response.data.message.passkey_auth[0]] })] } : 
          e.response.data.message;

        yield put(displayNotification(Types.NOTIFICATION_ERROR, message, notificationId));
      },
      unknown: function* () {
        let message = BITWARDEN_ERROR_TITLES_MESSAGES[e.response.data.message.passkey_auth[0]] ? 
          { passkey_auth: [intl.formatMessage({ id: BITWARDEN_ERROR_TITLES_MESSAGES[e.response.data.message.passkey_auth[0]] })] } : 
          e.response.data.message;

        yield put(displayNotification(Types.NOTIFICATION_ERROR, message, notificationId));
      },
    });
  }
}

export async function registerPasskeyApi(passkeyToken) {
  const p = new Client({
    apiUrl: getEnvVar('REACT_APP_PASSKEY_API_URL'),
    apiKey: getEnvVar('REACT_APP_PASSKEY_API_PUBLIC_KEY'),
  });

  const response = await p.register(passkeyToken);

  if (response?.error) {
    throw Object.assign(
      new Error(),
      {
        response: {
          status: response.error.errorCode,
          data: {
            message: {
              passkey_auth: [response.error.title],
            },
          }
        }
      
      }
    );
  }

  return response;
}

export function* loginPasskey() {
  try {
    const response = yield call(loginPasskeyApi);
    
    if (response) { 
      return response;
    }
  } catch (e) {
    yield errorCatch(e, {
      400: Messages.BAD_REQUEST,
      unknown: Messages.GENERIC_ERROR_MESSAGE
    });
  }
}

export async function loginPasskeyApi() {
  const p = new Client({
    apiUrl: getEnvVar('REACT_APP_PASSKEY_API_URL'),
    apiKey: getEnvVar('REACT_APP_PASSKEY_API_PUBLIC_KEY'),
  });

  const response = await p.signinWithDiscoverable();

  if (response?.error) {
    throw Object.assign(
      new Error(),
      {
        response: {
          status: response.error.errorCode,
          data: {
            message: {
              passkey_auth: [response.error.title],
            },
          }
        }
      
      }
    );
  }

  return response;
}

/**
 * Create a push notification subscription
 * @param {object} payload
 * @param {string} payload.notificationId
 * @param {object} payload.data
 * @returns {Promise<T>}
 */
export function createPushSubscription(payload) {
  return getAxiosWithoutLoading()
    .post(
      Endpoints.ENDPOINT_PUSH_SUBSCRIPTIONS,
      payload.data,
      getTokenHeaders()
    ).then(
      response => {
        if (response && response.status === 201) {
          return response;
        }

        return null;
      }
    ).catch(
      e => {
        errorCatch( e, {
          400: 'concat',
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
          404: Messages.NOT_FOUND_ERROR,
        }, getNotificationId( payload ) );
        return e.response;
      }
    );
}

/**
 * Delete a push notification subscription
 * @param {object} payload
 * @param {string} payload.notificationId
 * @param {object} payload.data
 * @returns {Promise<T>}
 */
export function deletePushSubscription(payload) {
  return getAxiosWithoutLoading()
    .delete(
      Endpoints.ENDPOINT_PUSH_SUBSCRIPTIONS,
      payload.data,
      getTokenHeaders()
    ).then(
      response => {
        if (response && response.status === 201) {
          return response;
        }

        return null;
      }
    ).catch(
      e => {
        errorCatch( e, {
          400: 'concat',
          401: Messages.UNAUTHENTICATED_ERROR,
          403: Messages.UNAUTHORIZED_ERROR,
          404: Messages.NOT_FOUND_ERROR,
        }, getNotificationId( payload ) );
        return e.response;
      }
    );
}